import io from "socket.io-client";
import Vue from "vue";
import { IEventSubscription } from "@/utils/event-subscription";
import { TokenStore } from "@/utils/token-store";

export interface ISocketService {
	on(event:string, handler:Function, ref?:any):void;
	once(event:string, handler:Function, ref?:any):void;
	off(event:string, handler:Function, ref?:any):void;
	offAll(ref:any):void;
	emit(event:string, data:any):void;
	connect():Promise<void>;
	disconnect():void;
	$on(event:string, handler:Function):void;
}

export const ERR_TOKEN_EXPIRED = "ERR_TOKEN_EXPIRED";

// eslint-disable-next-line max-lines-per-function
export function createSocketService(host:string, path:string):ISocketService{
	let socket = null as SocketIOClient.Socket | null;

	function updateSocketQuery(){
		(socket as any).query.token = TokenStore.accessToken;
	}

	return new Vue({
		data(){
			return {
				subs: [] as IEventSubscription[]
			};
		},
		methods: {
			on(event:string, handler:Function, ref?:any){
				console.info("Subscribing", event);
				this.addSub({event,handler,ref,once: false});
			},
			once(event:string, handler:Function, ref?:any){
				console.info("Subscribing", event, "once");
				this.addSub({event,handler,ref,once: true});
			},
			off(event:string, handler:Function, ref?:any){
				this.removeSub({event, handler, ref, once: false});
			},
			offAll(ref:any){
				if (!ref){
					return;
				}
				for (let i = this.subs.length-1; i >= 0; i--){
					let s = this.subs[i];
					if (s.ref == ref){
						this.subs.splice(i, 1);
						if (socket){
							console.info("Unsubscribing", s.event);
							socket.off(s.event, s.handler);
						}
					}
				}
			},
			emit(event:string, data:any){
				if (!socket) return;
				socket.emit(event, data);
			},

			addSub(sub:IEventSubscription){
				this.subs.push(sub);
				if (socket){
					if (sub.once){
						socket.once(sub.event, sub.handler);
						socket.once(sub.event, ()=>this.removeSub(sub));
					}else{
						socket.on(sub.event, sub.handler);
					}
				}
			},
			removeSub(sub:IEventSubscription){
				for (let i = this.subs.length-1; i >= 0; i--){
					let s = this.subs[i];
					if (s.event == sub.event && s.handler == sub.handler && s.ref == sub.ref){
						this.subs.splice(i, 1);
						if (socket){
							socket.off(sub.event, sub.handler);
						}
					}
				}
			},

			subscribeExistingEventsToSocket(){
				if (!socket) return;
				for (let sub of this.subs){
					if (sub.once){
						socket.once(sub.event, sub.handler);
						socket.once(sub.event, ()=>this.removeSub(sub));
					}else{
						socket.on(sub.event, sub.handler);
					}
				}
			},

			async connect():Promise<void>{
				return new Promise((resolve, reject)=>{
					if (socket && socket.connected){
						this.disconnect();
					}

					socket = io(host, { transports: ["websocket"], upgrade: false, path, reconnection: true, query: { token: TokenStore.accessToken }  });
					this.subscribeExistingEventsToSocket();
					updateSocketQuery();
					socket.on("reconnect_attempt", updateSocketQuery);

					let returned = false;
					socket.on("/init-error", (message:string)=>{
						this.disconnect();
						if (!returned){
							returned = true;
							reject(new Error(message));
						}

						if (message == "ERR_TOKEN_EXPIRED" || message == "TOKEN_EXPIRED"){
							this.$emit(ERR_TOKEN_EXPIRED);
						}
					});

					socket.on("connect", ()=>{
						if (!socket){
							if (!returned){
								returned = true;
								reject();
							}
							return;
						}

						if (!returned){
							returned = true;
							resolve();
						}
					});
				});
			},
			disconnect(){
				if (socket && socket.connected){
					socket.disconnect();
					socket.close();
				}
				socket = null;
			}
		},
		destroyed(){
			this.disconnect();
		}
	});
}